/**
 * The game's keyboard bindings, in one place. The handlers ask `matches(event, action)` and the Controls tab draws its keys from
 * `KEYMAP`, so a key cannot work without being listed (`tests/keymap.test.ts` checks every action is on the menu).
 */
export interface KeyAction {
  /** Menu group the key is listed under. */
  group: 'Party' | 'Orders' | 'Panels' | 'Camera';
  label: string;
  /** `KeyboardEvent.code` values that trigger it. */
  codes: readonly string[];
  /** The keycaps the menu draws; defaults to one per code. */
  caps?: readonly string[];
}

const DIGITS = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9', 'Digit0'] as const;

export const KEYMAP = {
  selectHero: { group: 'Party', label: 'Select a hero', codes: DIGITS, caps: ['1–9', '0'] },
  selectAll: { group: 'Party', label: 'Select all', codes: ['KeyA'] },
  run: { group: 'Orders', label: 'Run · walk', codes: ['KeyR'] },
  radial: { group: 'Orders', label: 'Radial menu', codes: ['KeyQ'] },
  plates: { group: 'Orders', label: 'Show plates and usable things (hold)', codes: ['Tab'] },
  pause: { group: 'Orders', label: 'Pause', codes: ['Space'] },
  cancel: { group: 'Orders', label: 'Cancel · close', codes: ['Escape'], caps: ['Esc'] },
  behavior: { group: 'Panels', label: 'Behavior', codes: ['KeyB'] },
  pack: { group: 'Panels', label: 'Pack', codes: ['KeyI'] },
  chart: { group: 'Panels', label: 'Chart', codes: ['KeyM'] },
  findParty: { group: 'Panels', label: 'Find the party', codes: ['KeyF'] },
  pan: { group: 'Camera', label: 'Pan', codes: ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'], caps: ['←', '↑', '→', '↓'] },
} as const satisfies Record<string, KeyAction>;

export type KeyActionId = keyof typeof KEYMAP;

/** Whether `event` is the action's key (modifiers are the handler's concern). */
export function matches(event: Pick<KeyboardEvent, 'code'>, action: KeyActionId): boolean {
  return (KEYMAP[action].codes as readonly string[]).includes(event.code);
}

/** The hero index (0 for the first) a number key picks, or -1 when the event is not one. */
export function heroSlot(event: Pick<KeyboardEvent, 'code'>): number {
  return (DIGITS as readonly string[]).indexOf(event.code);
}

/** The action's keycaps as the menu shows them. */
export function keycaps(action: KeyActionId): readonly string[] {
  const entry: KeyAction = KEYMAP[action];
  return entry.caps ?? entry.codes.map(code => code.replace(/^(Key|Digit)/, ''));
}

/** Every action of a menu group, in declaration order. */
export function actionsIn(group: KeyAction['group']): KeyActionId[] {
  return (Object.keys(KEYMAP) as KeyActionId[]).filter(id => KEYMAP[id].group === group);
}
